import React, { useState, useEffect, useRef } from "react";
import { AppProvider, useAppContext } from "@/AppContext";
import { ReactFlowProvider } from "@xyflow/react";
import PageTop from "@/Pages/Project/PageTop";
import PageSeletor from "@/Pages/Project/Flow/Right/PageSeleltor";
import { useLocation, useParams } from "react-router-dom";
// 引入临时任务数据
import { tempTasks } from "../tempConst";
// 引入数据映射
import { elementDesc, streamDesc, fieldMap, typeMap, InOutStreams } from "@/config/dataMap";
import { rebuildFormula } from "@/config/func";
import { getJob } from "@/services/common";
import userInfo from "@/Components/user";
import { Button, message } from "antd";
import "./../index.less";
import ReactJson from "react-json-view";
import { CopyOutlined } from "@ant-design/icons";

const Component = () => {
  const { state, actions } = useAppContext();
  const location = useLocation();
  const params = useParams<{ k: string; f: string; j: string }>();
  // 动态 Tab 列表（从数据中提取）
  const [tabList, setTabList] = useState<{ key: any; label: any }[]>([]);
  // Tab 切换状态
  const [activeTab, setActiveTab] = useState<any>("");
  // 当前任务数据
  const [currentTask, setCurrentTask] = useState<any>(null);
  // 解析后的input_data
  const [taskInputData, setTaskInputData] = useState<any>(null);
  // 当前选中的设备
  const [activeElement, setActiveElement] = useState<string | null>(null);
  // 当前选中的流股
  const [activeStream, setActiveStream] = useState<string | null>(null);
  const [input, setInput] = useState<any>({});

  // 整体查看模式
  const [showFullInput, setShowFullInput] = useState(false);

  // 控制设备/流股列表的展开/收起
  const [isDeviceOpen, setIsDeviceOpen] = useState(true);
  const [isStreamOpen, setIsStreamOpen] = useState(false);

  useEffect(() => {
    getJob(params.j, userInfo.access_token).then((r) => {
      if (r.status === 200) {
        setInput({
          name: r.data.name,
          flow_id: r.data.flow_id,
          description: r.data.description,
          inputs: JSON.parse(r.data.input),
        });
      }
    });
  }, []);

  useEffect(() => {
    if (input && input.inputs) {
      console.log("✅ input 已加载：", input);
      setTaskInputData(input);

      // 生成动态 Tab
      const tabs = generateTabListFromData(input);
      setTabList(tabs);
      // 默认选中第一个 Tab
      if (tabs.length > 0) {
        setActiveTab(tabs[0].key);
      }
    }
  }, [input]);

  // 从数据中提取所有不重复的 section 生成 Tab
  const generateTabListFromData = (data: any) => {
    if (!data?.inputs?.elements) return [];
    const hasSection = data.inputs.elements.some((el: any) => el.section);
    if (!hasSection) return [];

    const sections = data.inputs.elements.map((el: any) => el.section);
    const uniqueSections = Array.from(new Set(sections));
    return uniqueSections.map((sec) => ({ key: sec, label: sec }));
  };

  // 1. 提取筛选数据的逻辑为独立函数（方便复用）
  const getElementsByTab = (tabKey: string | null) => {
    if (!taskInputData?.inputs?.elements) return [];
    if (!tabKey) {
      return taskInputData.inputs.elements.filter((el: any) => el.type !== "Mixer");
    }
    return taskInputData.inputs.elements.filter(
      (el: any) => el.section === tabKey && el.type !== "Mixer",
    );
  };

  // 根据当前单元的设备，汇总所有流股
  const getStreamsByTab = (tabKey: string | null) => {
    if (!taskInputData?.inputs?.streams) return [];
    if (!tabKey) return taskInputData.inputs.streams;

    const sectionElements = taskInputData.inputs.elements.filter(
      (el: any) => el.section === tabKey,
    );
    const streamNames = new Set<string>();
    sectionElements.forEach((el: any) => {
      el.inputStreams?.forEach((s: any) => streamNames.add(s.name));
      el.outputStreams?.forEach((s: any) => streamNames.add(s.name));
    });
    return taskInputData.inputs.streams.filter((s: any) => streamNames.has(s.name));
  };

  // 2. 监听Tab切换 + 数据加载完成，自动选中第一个设备/流股
  useEffect(() => {
    if (params.j) {
      const taskId = parseInt(params.j);
      const task = tempTasks.find((item) => item.id === taskId);
      if (task) setCurrentTask(task);
    }
  }, [location.search, params.j]);

  // 3. 监听Tab切换，重新设置默认选中
  useEffect(() => {
    if (taskInputData && !showFullInput) {
      setDefaultSelection(tabList.length > 0 ? activeTab : null, taskInputData);
    }
    const tabElements = getElementsByTab(tabList.length > 0 ? activeTab : null);
    if (tabElements.length === 0) {
      setIsDeviceOpen(false); // 设备为空 → 收起设备
      setIsStreamOpen(true); // 自动展开流股
    }
  }, [activeTab, taskInputData, tabList, showFullInput]);

  // 4. 封装默认选中逻辑
  const setDefaultSelection = (tabKey: string | null, data: any) => {
    const tabElements = getElementsByTab(tabKey);
    const tabStreams = getStreamsByTab(tabKey);

    if (tabElements.length > 0) {
      setActiveElement(tabElements[0].name);
      setActiveStream(null);
    } else if (tabStreams.length > 0) {
      setActiveStream(tabStreams[0].name);
      setActiveElement(null);
    } else {
      setActiveElement(null);
      setActiveStream(null);
    }
  };

  // 5. 获取当前Tab的设备/流股（供渲染使用）
  const elements = getElementsByTab(tabList.length > 0 ? activeTab : null);
  const streams = getStreamsByTab(tabList.length > 0 ? activeTab : null);

  // 获取当前选中设备的完整数据
  const getActiveElementData = () => {
    if (!taskInputData?.inputs?.elements || !activeElement) return null;
    return taskInputData.inputs.elements.find((el: any) => el.name === activeElement);
  };

  // 获取当前选中流股的完整数据
  const getActiveStreamData = () => {
    if (!taskInputData?.inputs?.streams || !activeStream) return null;
    return taskInputData.inputs.streams.find((s: any) => s.name === activeStream);
  };

  // 渲染设备/流股基础参数行
  const renderParamRows = (data: any, type: "elements" | "streams") => {
    if (!data) return null;
    const fieldConfig = fieldMap[type];
    let params: any;
    if (type === "streams") {
      params = data.params;
    } else {
      params = { ...data, ...data.params };
    }
    return Object.entries(fieldConfig).map(([key, config]) => {
      if (params[key] === undefined || params[key] === null) return null;
      let value = config.enum?.[params[key]] ?? params[key];
      const unit = params[config.unitKey] ?? config.unit;
      return (
        <div key={key} className="paramRow">
          <span className="paramLabel">{config.name}</span>
          <span className="paramValue">
            {value} {unit && <span className="paramUnit">{unit}</span>}
          </span>
        </div>
      );
    });
  };

  // 渲染流股组分摩尔分率
  const renderComponentRows = (materials: any[]) => {
    if (!materials || materials.length === 0) return null;
    return materials.map((mat: any) => (
      <div key={mat.cas} className="paramRow">
        <span className="paramLabel">
          {rebuildFormula(mat.name)}（
          {mat.cas === "7732-18-5"
            ? "水"
            : mat.cas === "67-56-1"
              ? "甲醇"
              : rebuildFormula(mat.name)}
          ）
        </span>
        <span className="paramValue">{mat.molar_frac}</span>
      </div>
    ));
  };

  // 渲染进料流股位置
  const renderFeedPositions = (Ns: number[], inputStreams: any[]) => {
    if (!Ns || !inputStreams || Ns.length !== inputStreams.length) return null;
    return inputStreams.map((stream, idx) => (
      <div key={stream.name} className="paramRow">
        <span className="paramLabel">{`${stream.name}进料位置`}</span>
        <span className="paramValue">{Ns[idx]}</span>
      </div>
    ));
  };

  // 渲染出料流股位置
  const renderProductPositions = (No: number[], outputStreams: any[]) => {
    if (!No || !outputStreams || No.length !== outputStreams.length) return null;
    return outputStreams.map((stream, idx) => (
      <div key={stream.name} className="paramRow">
        <span className="paramLabel">{`${stream.name}出料位置`}</span>
        <span className="paramValue">{No[idx]}</span>
      </div>
    ));
  };

  // 处理设备/流股点击：互斥选中
  const handleElementClick = (name: string) => {
    setShowFullInput(false);
    setActiveElement(name);
    setActiveStream(null);
  };

  const handleStreamClick = (name: string) => {
    setShowFullInput(false);
    setActiveStream(name);
    setActiveElement(null);
  };

  // 一键复制
  const handleCopy = () => {
    const jsonStr = JSON.stringify(input, null, 2);
    navigator.clipboard.writeText(jsonStr).then(() => {
      message.success("复制成功！");
    });
  };

  return (
    <ReactFlowProvider>
      {state.curFlow && state.curProject && state.projectList ? (
        <>
          <PageTop
            mainContent={
              <div className="settingSelectorArea">
                <PageSeletor />
              </div>
            }
          />
          <div className="mainContent">
            <div className="mainTask">
              {/* 👇 Tab 栏：有 section 才显示 */}
              {tabList.length > 0 && (
                <div className="tabContainer">
                  <div className="tabTitle">计算参数：</div>
                  <div className="tabButtonGroup">
                    {tabList.map((tab) => (
                      <div
                        key={tab.key}
                        onClick={() => {
                          setShowFullInput(false);
                          setActiveTab(tab.key);
                        }}
                        className={`tabItem ${activeTab === tab.key ? "tabActive" : ""}`}
                      >
                        <span className="tabText">{tab.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="contentLayout">
                {/* 左侧设备/流股列表 */}
                <div className="sidebar">
                  <Button
                    type="primary"
                    style={{ marginBottom: 12, width: "100%" }}
                    onClick={() => {
                      setShowFullInput(true);
                      setActiveElement(null);
                      setActiveStream(null);
                      setIsDeviceOpen(false);
                      setIsStreamOpen(false);
                    }}
                  >
                    查看完整输入
                  </Button>

                  {/* 设备模块 - 可折叠 */}
                  <div className="sidebarSection">
                    <div
                      className="sidebarTitle"
                      onClick={() => setIsDeviceOpen(!isDeviceOpen)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      设备
                      {/* 箭头图标，可根据状态旋转 */}
                      <span
                        style={{
                          display: "inline-block",
                          transform: isDeviceOpen ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                          fontSize: "10px",
                          color: "#9a9a9bff",
                        }}
                      >
                        ▶
                      </span>
                    </div>
                    {/* 列表容器，根据状态控制显示/隐藏 */}
                    <div
                      className="sidebarList"
                      style={{
                        display: isDeviceOpen ? "block" : "none",
                        overflow: "hidden",
                        transition: "max-height 0.3s ease",
                      }}
                    >
                      {elements.map((el: any) => (
                        <div
                          key={el.name}
                          onClick={() => handleElementClick(el.name)}
                          className={`sidebarItem ${activeElement === el.name ? "sidebarItemActive" : ""}`}
                        >
                          {/* 设备名（中文名）备份 */}
                          {/* {el.name}（{elementDesc[el.name] || typeMap[el.type]}） */}
                          {el.name}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 流股模块 - 可折叠 */}
                  <div className="sidebarSection">
                    <div
                      className="sidebarTitle"
                      onClick={() => setIsStreamOpen(!isStreamOpen)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      流股
                      <span
                        style={{
                          display: "inline-block",
                          transform: isStreamOpen ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                          fontSize: "10px",
                          color: "#9a9a9bff",
                        }}
                      >
                        ▶
                      </span>
                    </div>
                    <div
                      className="sidebarList"
                      style={{
                        display: isStreamOpen ? "block" : "none",
                        overflow: "hidden",
                        transition: "max-height 0.3s ease",
                      }}
                    >
                      {streams.map((s: any) => (
                        <div
                          key={s.name}
                          onClick={() => handleStreamClick(s.name)}
                          className={`sidebarItem ${activeStream === s.name ? "sidebarItemActive" : ""}`}
                        >
                          {s.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 右侧参数展示区 */}
                <div className="paramPanel">
                  {/* 👇 整体查看：显示完整 input JSON */}
                  {showFullInput ? (
                    <div style={{ padding: "16px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: 12,
                        }}
                      >
                        <h2 style={{ margin: 0 }}>完整输入数据（input）</h2>
                        <Button type="primary" icon={<CopyOutlined />} onClick={handleCopy}>
                          一键复制
                        </Button>
                      </div>

                      <div className="json-container2">
                        <ReactJson
                          src={input}
                          collapsed={false}
                          // enableClipboard={true} // 显示复制按钮
                          displayDataTypes={false} // 隐藏数据类型标签
                          enableClipboard={false} // 关闭复制按钮
                          displayObjectSize={false} // 不显示 12 items
                          {...({ displayArrayKey: false } as any)}
                          indentWidth={2} // 缩进宽度
                          name={null} // 隐藏根节点名称
                          style={{ fontSize: "14px",fontFamily:"sans-serif, Arial,monospace" }}
                        />
                      </div>

                      {/* <pre
                        style={{
                          background: "#f7f8fa",
                          padding: "16px",
                          borderRadius: "8px",
                          maxHeight: "70vh",
                          overflow: "auto",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          fontSize: 13
                        }}
                      >
                        {JSON.stringify(input, null, 2)}
                      </pre> */}
                    </div>
                  ) : getActiveElementData() ? (
                    <div className="paramContent">
                      <h2 className="paramTitle">
                        {/* 设备名（中文名）备份 */}
                        {/* {getActiveElementData()?.name}（{elementDesc[getActiveElementData()?.name] || typeMap[getActiveElementData()?.type]}）参数 */}
                        {getActiveElementData()?.name} 参数
                      </h2>

                      {/* 基础参数（包含 physical_method/phase/mode 等顶层字段） */}
                      <div className="paramGrid">
                        {renderParamRows(getActiveElementData(), "elements")}
                      </div>

                      {/* 进料流股位置 */}
                      {getActiveElementData()?.params?.Ns &&
                        getActiveElementData()?.inputStreams && (
                          <>
                            <h3 className="sectionTitle">进料流股位置</h3>
                            <div className="paramGrid">
                              {renderFeedPositions(
                                getActiveElementData()?.params.Ns,
                                getActiveElementData()?.inputStreams,
                              )}
                            </div>
                          </>
                        )}

                      {/* 出料流股位置 */}
                      {getActiveElementData()?.params?.No &&
                        getActiveElementData()?.outputStreams && (
                          <>
                            <h3 className="sectionTitle">出料流股位置</h3>
                            <div className="paramGrid">
                              {renderProductPositions(
                                getActiveElementData()?.params.No,
                                getActiveElementData()?.outputStreams,
                              )}
                            </div>
                          </>
                        )}

                      {/* 塔板效率 */}
                      {getActiveElementData()?.params?.eff && (
                        <>
                          <h3 className="sectionTitle">塔板效率</h3>
                          <div className="paramGrid">
                            {getActiveElementData()?.params.eff.map((e: any, idx: number) => (
                              <>
                                <div key={`start-${idx}`} className="paramRow">
                                  <span className="paramLabel">起始塔板</span>
                                  <span className="paramValue">{e.start}</span>
                                </div>

                                <div key={`end-${idx}`} className="paramRow">
                                  <span className="paramLabel">结束塔板</span>
                                  <span className="paramValue">{e.end}</span>
                                </div>
                                <div key={`value-${idx}`} className="paramRow">
                                  <span className="paramLabel">效率值</span>
                                  <span className="paramValue">{e.value}</span>
                                </div>
                              </>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ) : getActiveStreamData() ? (
                    <div className="paramContent">
                      <h2 className="paramTitle">{getActiveStreamData()?.name}参数</h2>
                      <div className="streamDescRow">
                        <span className="paramLabel">流股说明</span>
                        <span className="paramValue">
                          {streamDesc[getActiveStreamData()?.name] || ""}
                        </span>
                      </div>
                      <div className="paramGrid">
                        {renderParamRows(getActiveStreamData(), "streams")}
                      </div>
                      {getActiveStreamData().materials?.length > 0 && (
                        <>
                          <h2 className="paramTitle componentTitle">
                            {getActiveStreamData()?.name}组分摩尔分率
                          </h2>
                          <div className="paramGrid componentGrid">
                            {renderComponentRows(getActiveStreamData()?.materials)}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="emptyTip">请选择设备或流股查看参数</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </ReactFlowProvider>
  );
};

export default Component;
